<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import apiClient from "@/apiClient.js";
import FormBox from "@/components/FormBox.vue";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import { MagnifyingGlassIcon, ArrowPathIcon, EnvelopeIcon } from "@heroicons/vue/24/solid";

const defaultFilters = Object.freeze({
  query: "",
  mode: "recent",
  invoiceMonths: 12,
  perPage: 25,
});

const filters = reactive({ ...defaultFilters });
const resultSet = ref({ data: [], current_page: 1, last_page: 1, total: 0 });
const loading = ref(false);
const errorMessage = ref("");
const clipboardMessage = ref("");
const exporting = ref(false);

const { setBreadcrumb, clearBreadcrumb } = useBreadcrumbs();

const contacts = computed(() => resultSet.value?.data ?? []);
const totalCount = computed(() => resultSet.value?.total ?? contacts.value.length);
const currentPage = computed(() => resultSet.value?.current_page ?? 1);
const lastPage = computed(() => resultSet.value?.last_page ?? 1);
const showPrev = computed(() => Boolean(resultSet.value?.prev_page_url));
const showNext = computed(() => Boolean(resultSet.value?.next_page_url));

const filterSummary = computed(() => {
  const parts = [];
  if (filters.mode === "recent") {
    parts.push(`Customers invoiced in last ${filters.invoiceMonths} months`);
  } else {
    parts.push("Lost lead contacts (quotes with no invoices)");
  }
  if (filters.query?.trim()) {
    parts.push(`matching "${filters.query.trim()}"`);
  }
  return parts.join(" • ");
});

const uniqueEmails = computed(() => {
  const seen = new Set();
  return contacts.value
    .map((contact) => contact?.email)
    .filter((email) => {
      if (!email || seen.has(email)) {
        return false;
      }
      seen.add(email);
      return true;
    });
});

const buildParams = (page) => {
  const params = {
    page,
    per_page: filters.perPage,
    mode: filters.mode,
  };
  const query = filters.query?.trim();
  if (query) {
    params.query = query;
  }
  if (filters.mode === "recent") {
    const months = Math.min(120, Math.max(1, Number(filters.invoiceMonths) || defaultFilters.invoiceMonths));
    params.invoice_months = months;
  }
  return params;
};

const fetchResults = async (page = 1) => {
  loading.value = true;
  errorMessage.value = "";
  try {
    const { data } = await apiClient.get("/api/customer/marketing-search", {
      params: buildParams(page),
    });
    resultSet.value = data;
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || "Unable to fetch contacts. Please try again.";
  } finally {
    loading.value = false;
  }
};

const applyFilters = () => {
  fetchResults(1);
};

const resetFilters = () => {
  Object.assign(filters, defaultFilters);
  fetchResults(1);
};

const changePage = (step) => {
  const targetPage = currentPage.value + step;
  if (targetPage < 1 || targetPage > lastPage.value) {
    return;
  }
  fetchResults(targetPage);
};

const copyEmails = async () => {
  clipboardMessage.value = "";
  if (!uniqueEmails.value.length) {
    clipboardMessage.value = "No emails available to copy.";
    setTimeout(() => (clipboardMessage.value = ""), 4000);
    return;
  }
  try {
    await navigator.clipboard.writeText(uniqueEmails.value.join(", "));
    clipboardMessage.value = `Copied ${uniqueEmails.value.length} emails.`;
  } catch (error) {
    clipboardMessage.value = "Could not copy emails to clipboard.";
  }
  setTimeout(() => {
    clipboardMessage.value = "";
  }, 4000);
};

const escapeCsv = (value) => {
  const stringValue = value ?? "";
  if (typeof stringValue !== "string") {
    return String(stringValue ?? "");
  }
  if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const exportCsv = async () => {
  if (!contacts.value.length) {
    clipboardMessage.value = "No data to export.";
    setTimeout(() => (clipboardMessage.value = ""), 3000);
    return;
  }
  exporting.value = true;
  try {
    const headers = ["Contact", "Email", "Phone", "Customers"];
    const rows = contacts.value.map((contact) => [
      escapeCsv(contact.name || ""),
      escapeCsv(contact.email || ""),
      escapeCsv(contact.phone || ""),
      escapeCsv(
        contact.customers && contact.customers.length
          ? contact.customers.map((customer) => customer.company).join("; ")
          : ""
      ),
    ]);
    const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `customer-marketing-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    clipboardMessage.value = "Unable to export CSV.";
    setTimeout(() => (clipboardMessage.value = ""), 3000);
  } finally {
    exporting.value = false;
  }
};

onMounted(() => {
  setBreadcrumb("Customer Marketing Search");
  fetchResults();
});

onBeforeUnmount(() => {
  clearBreadcrumb();
});
</script>

<template>
  <div class="space-y-6 text-gray-800 dark:text-slate-100">
    <FormBox header="Marketing Filters">
      <form class="space-y-4" @submit.prevent="applyFilters">
        <div class="grid gap-4 lg:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm font-medium text-gray-700 dark:text-slate-200">
            Contact keywords
            <input type="text" v-model="filters.query" placeholder="Name, email or phone"
              class="input" @keyup.enter="applyFilters" />
          </label>

          <label class="flex flex-col gap-1 text-sm font-medium text-gray-700 dark:text-slate-200">
            Results per page
            <select v-model.number="filters.perPage" class="input" @change="applyFilters">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </label>
        </div>

        <div class="space-y-2">
          <p class="text-sm font-semibold text-gray-700 dark:text-slate-200">Who are you looking for?</p>
          <div class="flex flex-col gap-3 lg:flex-row">
            <label
              :class="['flex flex-1 items-center gap-3 rounded px-4 py-3 border text-sm', filters.mode === 'recent'
                ? 'border-blue-500 bg-blue-50 dark:bg-transparent'
                : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-transparent']">
              <input type="radio" class="h-4 w-4" value="recent" v-model="filters.mode" />
              <div class="flex-1">
                <p class="font-medium">Customers in X months</p>
                <div class="flex items-center gap-2 mt-1 text-xs">
                  Last
                  <input type="number" class="w-16 input px-2 py-1" min="1" max="120" v-model.number="filters.invoiceMonths"
                    :disabled="filters.mode !== 'recent'" />
                  months
                </div>
              </div>
            </label>
            <label
              :class="['flex flex-1 items-center gap-3 rounded px-4 py-3 border text-sm', filters.mode === 'lost_lead'
                ? 'border-blue-500 bg-blue-50 dark:bg-transparent'
                : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-transparent']">
              <input type="radio" class="h-4 w-4" value="lost_lead" v-model="filters.mode" />
              <div class="flex-1">
                <p class="font-medium">Lost lead</p>
                <p class="text-xs text-gray-500 dark:text-slate-300">
                  Contacts with quote/quotation files and zero invoices.
                </p>
              </div>
            </label>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-3 pt-2">
          <button type="submit" class="btn save flex items-center gap-2" :disabled="loading">
            <MagnifyingGlassIcon class="icon" />
            Search
          </button>
          <button type="button" class="btn flex items-center gap-2" @click="resetFilters" :disabled="loading">
            <ArrowPathIcon class="icon" />
            Reset
          </button>
          <span class="text-sm text-gray-500 dark:text-slate-300">
            {{ filterSummary }}
          </span>
        </div>
      </form>
    </FormBox>

    <FormBox header="Matching Contacts">
      <template #nav>
        <div class="flex gap-2">
          <button class="btn flex items-center gap-2" @click.prevent="exportCsv" :disabled="!contacts.length || exporting">
            <ArrowPathIcon v-if="exporting" class="icon animate-spin" />
            <span v-else>Export CSV</span>
          </button>
          <button class="btn accent flex items-center gap-2" @click.prevent="copyEmails" :disabled="!uniqueEmails.length">
            <EnvelopeIcon class="icon" />
            Copy Emails
          </button>
        </div>
      </template>

      <div class="text-sm text-gray-600 dark:text-slate-300 mb-3 flex flex-col gap-1">
        <span>
          Showing {{ contacts.length }} of {{ totalCount }} results (page {{ currentPage }} / {{ lastPage }})
        </span>
        <span v-if="uniqueEmails.length">
          {{ uniqueEmails.length }} unique contact emails ready to copy.
        </span>
        <span v-if="clipboardMessage" class="text-green-600 dark:text-green-400">
          {{ clipboardMessage }}
        </span>
      </div>

      <p v-if="errorMessage"
        class="p-3 rounded border border-red-200 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-950 dark:text-red-200 text-sm mb-4">
        {{ errorMessage }}
      </p>

      <div v-if="loading" class="py-10 text-center text-gray-500 dark:text-slate-400">
        Loading contacts...
      </div>

      <div v-else>
        <div v-if="!contacts.length" class="py-8 text-center text-gray-500 dark:text-slate-400">
          No contacts match the current filters.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="box list text-sm">
            <thead class="hidden xl:table-header-group">
              <tr>
                <th class="px-3 pt-2 text-left">Contact</th>
                <th class="px-3 pt-2 text-left">Email</th>
                <th class="px-3 pt-2 text-left">Phone</th>
                <th class="px-3 pt-2 text-left">Customers</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="contact in contacts" :key="contact.id"
                class="border-b border-white dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800">
                <td class="px-3 py-3">
                  <p class="font-medium">{{ contact.name }}</p>
                  <p class="text-xs text-gray-500 dark:text-slate-400" v-if="contact.email">
                    {{ contact.email }}
                  </p>
                </td>
                <td class="px-3 py-3">
                  <router-link
                    v-if="contact.email && contact.id"
                    class="text-blue-600 dark:text-blue-400 hover:underline"
                    :to="{ name: 'contact-view', params: { id: contact.id } }"
                  >
                    {{ contact.email }}
                  </router-link>
                  <span v-else-if="contact.email">{{ contact.email }}</span>
                  <span v-else class="text-gray-400 dark:text-slate-500">—</span>
                </td>
                <td class="px-3 py-3">
                  {{ contact.phone || "—" }}
                </td>
                <td class="px-3 py-3">
                  <template v-if="contact.customers && contact.customers.length">
                    <span v-for="customer in contact.customers" :key="customer.id"
                      class="inline-block text-xs px-2 py-1 mr-2 mb-1 rounded bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
                      {{ customer.company }}
                    </span>
                  </template>
                  <span v-else class="text-gray-400 dark:text-slate-500">No customer</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pt-4 flex justify-center" v-if="contacts.length">
          <PrevNextButtons :showPrev="showPrev" :showNext="showNext" @prev="changePage(-1)" @next="changePage(1)" />
        </div>
      </div>
    </FormBox>
  </div>
</template>

<style scoped>
.input {
  @apply border border-gray-300 dark:border-slate-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-blue-200 dark:bg-slate-800 dark:focus:ring-blue-500 dark:text-slate-100;
}
</style>
